import React, { useState } from 'react';
import { FlaskConical, Play, RotateCcw, CheckCircle, Loader2, DollarSign, AlertTriangle } from 'lucide-react';

interface TestResult {
  variant: 'A' | 'B';
  response: string;
  tokens: number;
  cost: number;
  rating: number | null;
}

export const PromptTestingPage: React.FC = () => {
  const [promptA, setPromptA] = useState('Du bist ein freundlicher Fahrlehrer. Erklaere die Antwort kurz und verstaendlich.');
  const [promptB, setPromptB] = useState('Du bist ein Experte fuer Strassenverkehr. Gib eine ausfuehrliche, didaktische Erklaerung mit Beispielen.');
  const [testQuestion, setTestQuestion] = useState('Warum muss man bei Nebel das Abblendlicht einschalten?');
  const [results, setResults] = useState<TestResult[]>([]);
  const [testing, setTesting] = useState(false);
  const [costAlert, setCostAlert] = useState(false);
  const [monthlyBudget, setMonthlyBudget] = useState(10);
  const [currentCost] = useState(0);

  const runTest = async () => {
    setTesting(true);
    // Simulate A/B test responses (in production would call Gemini API)
    await new Promise(r => setTimeout(r, 1500));

    const resultA: TestResult = {
      variant: 'A', rating: null,
      response: 'Bei Nebel ist die Sicht stark eingeschraenkt. Das Abblendlicht sorgt dafuer, dass du selbst besser siehst und von anderen Verkehrsteilnehmern erkannt wirst. Fernlicht wuerde vom Nebel reflektiert und blendet.',
      tokens: 142, cost: 0.00008,
    };
    const resultB: TestResult = {
      variant: 'B', rating: null,
      response: 'Nebel reduziert die Sichtweite erheblich. Das Abblendlicht hat zwei wichtige Funktionen:\n\n1. **Eigene Sicht**: Die Lichtstrahlen werden flach auf die Strasse gerichtet, was die Sicht verbessert.\n2. **Erkennbarkeit**: Andere Verkehrsteilnehmer koennen dich frueher sehen.\n\n**Warum kein Fernlicht?** Fernlicht strahlt nach oben und wird von den Wassertroepfchen im Nebel zurueck reflektiert — das blendet dich selbst (sogenannte "weisse Wand").\n\n**Tipp**: Bei Sichtweiten unter 50m darfst du auch die Nebelscheinwerfer und die Nebelschlussleuchte nutzen.',
      tokens: 312, cost: 0.00018,
    };

    setResults([resultA, resultB]);
    setTesting(false);
  };

  const rateResult = (variant: 'A' | 'B', rating: number) => {
    setResults(prev => prev.map(r => r.variant === variant ? { ...r, rating } : r));
  };

  const winner = results.length === 2 && results.every(r => r.rating !== null)
    ? results[0].rating! > results[1].rating! ? 'A' : results[1].rating! > results[0].rating! ? 'B' : 'Unentschieden'
    : null;

  return (
    <div>
      <div className="page-header"><h1><FlaskConical size={24} /> Prompt A/B-Testing</h1></div>

      {/* Cost Alert */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 12 }}><DollarSign size={18} /> Kosten-Alert</h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm">Monatsbudget ($):</label>
            <input className="input" type="number" style={{ width: 80 }} value={monthlyBudget} onChange={e => setMonthlyBudget(Number(e.target.value))} />
          </div>
          <div className="text-sm">Aktuell: <strong>${currentCost.toFixed(2)}</strong> / ${monthlyBudget}</div>
          <div style={{ flex: 1, height: 8, background: 'var(--border-light)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min((currentCost / monthlyBudget) * 100, 100)}%`, height: '100%', background: currentCost / monthlyBudget > 0.8 ? 'var(--danger)' : 'var(--success)', borderRadius: 4 }} />
          </div>
          {currentCost / monthlyBudget > 0.8 && (
            <span className="badge badge-danger"><AlertTriangle size={12} /> Nahe am Limit!</span>
          )}
        </div>
      </div>

      {/* Test Setup */}
      <div className="grid-cols-2 mb-6">
        <div className="card">
          <h3 style={{ marginBottom: 8 }}>Variante A</h3>
          <textarea className="input" rows={3} value={promptA} onChange={e => setPromptA(e.target.value)} placeholder="System-Prompt A..." />
        </div>
        <div className="card">
          <h3 style={{ marginBottom: 8 }}>Variante B</h3>
          <textarea className="input" rows={3} value={promptB} onChange={e => setPromptB(e.target.value)} placeholder="System-Prompt B..." />
        </div>
      </div>

      <div className="card mb-6">
        <label className="text-sm" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Test-Frage:</label>
        <div className="flex gap-2">
          <input className="input" style={{ flex: 1 }} value={testQuestion} onChange={e => setTestQuestion(e.target.value)} placeholder="Frage eingeben..." />
          <button className="btn btn-primary" onClick={runTest} disabled={testing}>
            {testing ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={14} />}
            Test starten
          </button>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <>
          <div className="grid-cols-2 mb-6">
            {results.map(r => (
              <div key={r.variant} className="card" style={{ borderColor: winner === r.variant ? 'var(--success)' : undefined, borderWidth: winner === r.variant ? 2 : undefined }}>
                <div className="flex items-center justify-between mb-3">
                  <h3>Variante {r.variant} {winner === r.variant && <span className="badge badge-success">Gewinner</span>}</h3>
                  <div className="flex gap-2 text-xs text-muted">
                    <span>{r.tokens} Tokens</span>
                    <span>${r.cost.toFixed(5)}</span>
                  </div>
                </div>
                <div style={{ background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {r.response}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted">Bewertung:</span>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => rateResult(r.variant, n)} style={{
                      background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
                      opacity: r.rating && n <= r.rating ? 1 : 0.3,
                    }}>⭐</button>
                  ))}
                  {r.rating && <span className="text-sm" style={{ fontWeight: 600 }}>{r.rating}/5</span>}
                </div>
              </div>
            ))}
          </div>

          {winner && winner !== 'Unentschieden' && (
            <div className="alert alert-success">
              <CheckCircle size={16} /><span>Variante {winner} hat gewonnen! Uebernimm den Prompt unter KI-Prompts.</span>
            </div>
          )}

          <div className="flex justify-center">
            <button className="btn btn-secondary" onClick={() => { setResults([]); }}>
              <RotateCcw size={14} /> Neuer Test
            </button>
          </div>
        </>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
